import { useEffect, useId, useState } from "react";
import SystemIcon from "./SystemIcon";

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function range(start, end) {
  const values = [];
  for (let value = start; value <= end; value += 1) values.push(value);
  return values;
}

function buildPages(page, total) {
  if (total <= 5) return range(1, total);
  if (page <= 3) return [1, 2, 3, 4, "ellipsis-end", total];
  if (page >= total - 2) return [1, "ellipsis-start", total - 3, total - 2, total - 1, total];
  return [1, "ellipsis-start", page - 1, page, page + 1, "ellipsis-end", total];
}

export default function Pagination({
  page,
  pageCount,
  onChange,
  busy = false,
  disabled = false,
  totalPages,
  setPage,
  showJump = true,
  className = "",
}) {
  const changePage = onChange ?? setPage;
  const safePageCount = Math.max(1, Number(pageCount ?? totalPages) || 1);
  const safePage = clamp(Number(page) || 1, 1, safePageCount);
  const locked = Boolean(busy || disabled);
  const pages = buildPages(safePage, safePageCount);
  const [jumpValue, setJumpValue] = useState(String(safePage));
  const jumpInputId = useId();

  useEffect(() => setJumpValue(String(safePage)), [safePage]);

  function go(nextPage) {
    if (locked || typeof changePage !== "function") return;
    const next = clamp(nextPage, 1, safePageCount);
    if (next !== safePage) changePage(next);
  }

  function submitJump(event) {
    event.preventDefault();
    const next = Number.parseInt(jumpValue, 10);
    if (!Number.isFinite(next) || next < 1 || next > safePageCount) {
      setJumpValue(String(safePage));
      return;
    }
    go(next);
  }

  return (
    <div
      className={`ds-pagination${locked ? " is-locked" : ""}${className ? ` ${className}` : ""}`}
      aria-busy={busy || undefined}
    >
      <nav className="ds-pagination__nav" aria-label="페이지 이동">
        <button
          type="button"
          className="ds-pagination__button ds-pagination__button--direction"
          onClick={() => go(safePage - 1)}
          disabled={locked || safePage <= 1}
          aria-label="이전 페이지"
        >
          <SystemIcon name="angle" size="small" direction="left" />
        </button>

        <div className="ds-pagination__pages">
          {pages.map((item) => (
            typeof item === "number" ? (
              <button
                key={item}
                type="button"
                className={`ds-pagination__button${item === safePage ? " is-current" : ""}`}
                onClick={() => go(item)}
                disabled={locked}
                aria-current={item === safePage ? "page" : undefined}
                aria-label={`${item}페이지`}
              >
                {item}
              </button>
            ) : (
              <span key={item} className="ds-pagination__ellipsis" aria-hidden="true">
                <SystemIcon name="ellipsis" size="small" />
              </span>
            )
          ))}
        </div>

        <button
          type="button"
          className="ds-pagination__button ds-pagination__button--direction"
          onClick={() => go(safePage + 1)}
          disabled={locked || safePage >= safePageCount}
          aria-label="다음 페이지"
        >
          <SystemIcon name="angle" size="small" direction="right" />
        </button>
      </nav>

      {showJump && safePageCount > 1 ? (
        <form className="ds-pagination__jump" onSubmit={submitJump}>
          <label htmlFor={jumpInputId}>페이지</label>
          <input
            id={jumpInputId}
            type="number"
            inputMode="numeric"
            min="1"
            max={safePageCount}
            value={jumpValue}
            onChange={(event) => setJumpValue(event.target.value)}
            onBlur={() => {
              const next = Number.parseInt(jumpValue, 10);
              if (!Number.isFinite(next) || next < 1 || next > safePageCount) {
                setJumpValue(String(safePage));
              }
            }}
            disabled={locked}
            aria-label="이동할 페이지"
          />
          <span>/ {safePageCount}</span>
          <button type="submit" disabled={locked}>이동</button>
        </form>
      ) : null}
    </div>
  );
}
